import { format } from 'date-fns';
import { canManageSessions } from './roleUtils';
import { isHostSelectSignup } from './signupSelection';

/**
 * Utility functions for Home dashboard session data processing
 */

/**
 * Determine user's relationship to a session
 * @param {Object} session - Session object
 * @param {string} playerId - Current user's player ID
 * @param {Object} signupMap - Map of session_id to signup status
 * @param {Array} sessionPlayerRows - Array of session_players rows
 * @returns {'player' | 'host' | 'both' | 'none'}
 */
const ROSTER_STATUSES = new Set(['active', 'signed_up', 'paused']);

export function isPlayerOnSessionRoster(sessionId, sessionPlayerRows = []) {
  return sessionPlayerRows.some(
    (row) => row.session_id === sessionId && ROSTER_STATUSES.has(row.status)
  );
}

export function getUserRelationshipToSession(session, playerId, signupMap, sessionPlayerRows) {
  const isHost = session.host_player_id === playerId;
  
  // Check if player is in session_players for this session
  const isInSessionPlayers = isPlayerOnSessionRoster(session.id, sessionPlayerRows);
  
  // Check if player has confirmed/waitlist signup
  const signup = signupMap.get(session.id);
  const hasActiveSignup = signup && ['confirmed', 'waitlist'].includes(signup.signup_status);
  
  const isPlayer = isInSessionPlayers || hasActiveSignup;
  
  if (isHost && isPlayer) return 'both';
  if (isHost) return 'host';
  if (isPlayer) return 'player';
  return 'none';
}

/**
 * Determine if a user can manage a session
 * @param {Object} session - Session object
 * @param {string} playerId - Current user's player ID
 * @param {string} siteRole - User's site role
 * @param {Map} clubRolesMap - Map of club_id to club_role
 * @returns {boolean}
 */
export function canManageSession(session, playerId, siteRole, clubRolesMap) {
  // User is the host
  if (session.host_player_id === playerId) {
    return true;
  }
  
  // User has club management permissions
  const clubRole = clubRolesMap.get(session.club_id);
  if (clubRole && canManageSessions(siteRole, clubRole)) {
    return true;
  }
  
  return false;
}

/**
 * Determine if a user can join a session
 * @param {Object} session - Session object
 * @param {string} relationship - User's relationship to session
 * @param {Map} signupMap - Map of session_id to signup status
 * @returns {boolean}
 */
export function canJoinSession(session, relationship, signupMap) {
  // Already participating or hosting
  if (relationship === 'player' || relationship === 'both') {
    return false;
  }
  
  // Session must be upcoming
  if (session.session_status !== 'upcoming') {
    return false;
  }
  
  // Must be public or user is already related
  if (!session.is_public && relationship === 'none') {
    return false;
  }
  
  // Check if already signed up
  const signup = signupMap.get(session.id);
  if (signup && ['confirmed', 'waitlist'].includes(signup.signup_status)) {
    return false;
  }
  
  if (isHostSelectSignup(session)) {
    return true;
  }

  // Check if session is full and waitlist is disabled
  const confirmedCount = session.confirmed_players || 0;
  const maxPlayers = session.max_players || 16;
  const isFull = confirmedCount >= maxPlayers;
  
  if (isFull && !session.waitlist_enabled) {
    return false;
  }
  
  return true;
}

/**
 * Get the appropriate CTA for a session
 * @param {Object} session - Session object
 * @param {string} relationship - User's relationship to session
 * @param {boolean} canManage - Whether user can manage the session
 * @param {boolean} canJoin - Whether user can join the session
 * @returns {{ label: string, action: string, variant: string }}
 */
export function getSessionCTA(session, relationship, canManage, canJoin) {
  if (canManage) {
    return { label: 'Manage', action: 'manage', variant: 'primary' };
  }
  
  if (relationship === 'player' || relationship === 'both') {
    return { label: 'View', action: 'view', variant: 'outline' };
  }
  
  if (canJoin) {
    if (isHostSelectSignup(session)) {
      return { label: 'Sign up', action: 'join', variant: 'primary' };
    }
    const confirmedCount = session.confirmed_players || 0;
    const maxPlayers = session.max_players || 16;
    const isFull = confirmedCount >= maxPlayers;
    
    if (isFull && session.waitlist_enabled) {
      return { label: 'Join Waitlist', action: 'join', variant: 'outline' };
    }
    return { label: 'Join', action: 'join', variant: 'primary' };
  }
  
  return { label: 'View', action: 'view', variant: 'outline' };
}

/**
 * Map a session to Home dashboard display format
 * @param {Object} session - Raw session object from Supabase
 * @param {string} playerId - Current user's player ID
 * @param {string} siteRole - User's site role
 * @param {Map} clubRolesMap - Map of club_id to club_role
 * @param {Map} signupMap - Map of session_id to signup data
 * @param {Array} sessionPlayerRows - Array of session_players rows for this user
 * @returns {Object} Enriched session object for display
 */
export function mapSessionToHomeItem(session, playerId, siteRole, clubRolesMap, signupMap, sessionPlayerRows) {
  const relationship = getUserRelationshipToSession(session, playerId, signupMap, sessionPlayerRows);
  const canManage = canManageSession(session, playerId, siteRole, clubRolesMap);
  const canJoin = canJoinSession(session, relationship, signupMap);
  const cta = getSessionCTA(session, relationship, canManage, canJoin);
  
  return {
    ...session,
    userRelationship: relationship,
    permissions: {
      canManage,
      canJoin
    },
    cta,
    // Ensure these fields are present
    confirmedPlayers: session.confirmed_players || 0,
    waitlistPlayers: session.waitlist_players || 0,
    playersInSession: session.players_in_session,
    clubName: session.clubs?.name || 'Unknown Club',
    clubLogoImageUrl: session.clubs?.logo_image_url || null,
    sessionTypeName: session.session_types?.name || null,
    isInSessionRoster: isPlayerOnSessionRoster(session.id, sessionPlayerRows),
  };
}

/**
 * Sort sessions by date and time (earliest first)
 * @param {Array} sessions - Array of session objects
 * @returns {Array} Sorted sessions
 */
export function sortSessionsByDateTime(sessions) {
  return [...sessions].sort((a, b) => {
    // Sort by session_date first
    const dateCompare = (a.session_date || '').localeCompare(b.session_date || '');
    if (dateCompare !== 0) return dateCompare;
    
    // Then by start_time
    return (a.start_time || '').localeCompare(b.start_time || '');
  });
}

/**
 * Format DB time (`HH:mm` or `HH:mm:ss`) for display without seconds.
 */
export function formatSessionTimeDisplay(timeString) {
  if (!timeString || timeString === 'TBD') return 'TBD';
  const clean = String(timeString).trim();
  const base = clean.split('+')[0].split('Z')[0];
  if (!base.includes(':')) return clean;

  let hoursPart;
  let minutesPart;
  if (base.includes('T') && /\d{2}:\d{2}/.test(base)) {
    const timePortion = base.split('T').pop();
    const p = timePortion.split(':');
    hoursPart = p[0];
    minutesPart = p[1];
  } else {
    const p = base.split(':');
    hoursPart = p[0];
    minutesPart = p[1];
  }

  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart !== undefined ? minutesPart : '0', 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return clean.slice(0, Math.min(clean.length, 8));

  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return format(d, 'h:mm a');
}

/** Start–end clock from TIME columns, not session_date (midnight UTC). */
export function formatSessionTimeRange(startTime, endTime, { compact = false } = {}) {
  const start = formatSessionTimeDisplay(startTime);
  const end = endTime ? formatSessionTimeDisplay(endTime) : null;
  const hasEnd = Boolean(end && end !== 'TBD' && end !== start);
  if (!hasEnd) return start;
  if (compact) {
    const startPeriod = start.slice(-2);
    const endPeriod = end.slice(-2);
    if (startPeriod === endPeriod) return `${start.slice(0, -3)}–${end}`;
  }
  return `${start} – ${end}`;
}

/** Inclusive local-date window from today (0) through `days` ahead. */
export function isSessionDateWithinDays(session, days = 14) {
  const datePart = String(session?.session_date || '').split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
  const [year, month, day] = datePart.split('-').map(Number);
  const sessionDay = new Date(year, month - 1, day);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return sessionDay >= start && sessionDay <= end;
}

/**
 * Ongoing sessions: prefer the most recent calendar date/time (typically "today's live").
 */
export function sortOngoingForNextGame(sessions) {
  return [...sessions].sort((a, b) => {
    const dateCompare = (b.session_date || '').localeCompare(a.session_date || '');
    if (dateCompare !== 0) return dateCompare;
    return (b.start_time || '').localeCompare(a.start_time || '');
  });
}

/**
 * Filter sessions to only future/upcoming ones
 * @param {Array} sessions - Array of session objects
 * @returns {Array} Filtered sessions
 */
export function filterUpcomingSessions(sessions) {
  const today = new Date().toISOString().split('T')[0];
  
  return sessions.filter(session => {
    // Must be upcoming status
    if (session.session_status !== 'upcoming') {
      return false;
    }
    
    // Must be today or future
    if (session.session_date && session.session_date < today) {
      return false;
    }
    
    return true;
  });
}

/**
 * Deduplicate sessions by ID, preferring the most complete data
 * @param {Array} sessions - Array of potentially duplicate session objects
 * @returns {Array} Deduplicated sessions
 */
export function deduplicateSessions(sessions) {
  const sessionMap = new Map();
  
  sessions.forEach(session => {
    if (!session?.id) return;
    
    const existing = sessionMap.get(session.id);
    if (!existing) {
      sessionMap.set(session.id, session);
    } else {
      // Merge data, preferring non-null values
      sessionMap.set(session.id, {
        ...existing,
        ...session,
        // Keep arrays from both if they exist
        session_signups: session.session_signups || existing.session_signups || [],
        session_players: session.session_players || existing.session_players || []
      });
    }
  });
  
  return Array.from(sessionMap.values());
}
